import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators, FormBuilder } from '@angular/forms';
import { SelectItem, LazyLoadEvent, ConfirmationService } from 'primeng';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IInsuranceProvider } from '../../interfaces/insurance-provider';
import { IInsuranceProviderFilter } from '../../interfaces/insurance-provider-filter';
import { IPageResult } from '../../interfaces/page-result';
import { InsuranceProvidersService } from '../../services';
import { InputHelper } from '../../utils/input.helper';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';
import moment from 'moment';

@Component({
  selector: 'page-insurance-providers',
  templateUrl: './insurance-providers.component.html',
})
export class InsuranceProvidersComponent implements OnInit {
  msgs: Message[] = [];

  public providerForm: UntypedFormGroup;

  periodFrom: Date;
  periodTo: Date;

  displayDialog: boolean = false;
  selectedItem: any;

  public page: IPageResult<IInsuranceProvider> = {
    items: [],
    total: 0,
  };

  public isLoaded: boolean = false;
  public filterVal: IInsuranceProviderFilter;
  public filter: Subject<IInsuranceProviderFilter>;

  constructor(
    private router: Router,
    private insuranceProviderService: InsuranceProvidersService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public inputHelper: InputHelper,
  ) {}

  ngOnInit() {
    this.initDefaultFilter();
    this.filter = new Subject<IInsuranceProviderFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next(this.filterVal);

    // set formGroup instance

    this.providerForm = new UntypedFormGroup({
      id: new UntypedFormControl(0),
      insuranceProvider: new UntypedFormControl(
        '',
        Validators.compose([Validators.required, Validators.maxLength(50)]),
      ),
      periodFrom: new UntypedFormControl('', (validator: UntypedFormControl) => {
        // Ignore time aspect
        var to = new Date(this.periodTo);
        to.setHours(0, 0, 0, 0);
        var from = new Date(validator.value);
        from.setHours(0, 0, 0, 0);

        if (to < from) {
          validator.markAsDirty();
          return { valid: false, error: 'This perionTo must be greater than periodFrom' };
        }

        return null;
      }),
      periodTo: new UntypedFormControl('', (validator: UntypedFormControl) => {
        // Ignore time aspect
        var from = new Date(this.periodFrom);
        from.setHours(0, 0, 0, 0);
        var to = new Date(validator.value);
        to.setHours(0, 0, 0, 0);

        if (to < from) {
          validator.markAsDirty();
          return { valid: false, error: 'This perionTo must be greater than periodFrom' };
        }

        return null;
      }),
      callOutFee: new UntypedFormControl(0, Validators.compose([Validators.required])),
      mileageCap: new UntypedFormControl(0, Validators.compose([Validators.required])),
      mileageCost: new UntypedFormControl(0, Validators.compose([Validators.required])),
      mileageCost2: new UntypedFormControl(0, Validators.compose([Validators.required])),
    });
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: '',
      sortOrder: 1,
      filters: {
        insuranceProvider: { value: null },
      },
    };
  }

  load(filter: IInsuranceProviderFilter) {
    this.insuranceProviderService.getPage(filter).then((result) => {
      if (result) {
        this.page.items = result.items;
        this.page.total = result.total;
      }

      this.isLoaded = true;
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filter.next(this.filterVal);
  }

  search() {
    this.load(this.filterVal);
  }

  clearFilter() {
    this.initDefaultFilter();
    this.search();
  }

  resetFormValues(formValues) {
    this.periodFrom = formValues.periodFrom;
    this.periodTo = formValues.periodTo;

    this.providerForm.setValue({
      id: formValues.id,
      insuranceProvider: formValues.insuranceProvider,
      periodFrom: formValues.periodFrom ? moment(formValues.periodFrom).toDate() : '',
      periodTo: formValues.periodFrom ? moment(formValues.periodTo).toDate() : '',
      callOutFee: formValues.callOutFee,
      mileageCap: formValues.mileageCap,
      mileageCost: formValues.mileageCost,
      mileageCost2: formValues.mileageCost2,
    });
  }

  showDialogToAdd() {
    this.resetFormValues({
      id: 0,
      insuranceProvider: '',
      periodFrom: '',
      periodTo: '',
      callOutFee: 0,
      mileageCap: 0,
      mileageCost: 0,
      mileageCost2: 0,
    });
    this.displayDialog = true;
  }

  save(formValues) {
    if (this.periodFrom) {
      formValues.periodFrom = moment(this.periodFrom).format('YYYY-MM-DD HH:mm');
    }

    if (this.periodTo) {
      formValues.periodTo = moment(this.periodTo).format('YYYY-MM-DD HH:mm');
    }

    this.insuranceProviderService.save(formValues).then((res) => this.load(this.filterVal));
    this.displayDialog = false;
  }

  close() {
    this.displayDialog = false;
  }

  onEdit(item) {
    this.resetFormValues(item);
    this.displayDialog = true;
  }

  onDelete(item) {
    this.confirmationService.confirm({
      message: `Are you sure want to delete selected item?`,
      accept: () => {
        this.insuranceProviderService
          .deleteItem(item)
          .then((res) => this.load(this.filterVal))
          .catch((e) => {
            this.messageService.add(
              Message.Error(
                'Error while deleting provider - there are jobs which use this provider.',
                'Delete Insurance Provider',
              ),
            );
          });
      },
    });
  }
}
